import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getSupabase } from "@/lib/supabaseClient";
import { StatusBadge, STATUS_OPTIONS } from "@/components/StatusBadge";
import {
  Flame, LogOut, Ticket, Trash2, Users, Send, Mail, Filter, Plus, ShieldCheck, X, Inbox, Handshake,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const inputCls = "bg-[#262D38] border-[#2A313C] focus:border-[#E11D48] focus-visible:ring-[#E11D48]/30 text-white placeholder:text-slate-500 rounded-xl";
const card = "rounded-2xl border border-[#2A313C] bg-[#1F242D]";

const SOURCE_LABELS = { marketing_site: "Marketing Site", web_form: "Web Form", portal: "Web Form" };
const sourceLabel = (s) => SOURCE_LABELS[s] || "Web Form";

function StatCard({ label, value, tone }) {
  return (
    <div className={`${card} p-5`}>
      <p className="text-sm text-slate-400">{label}</p>
      <p className="mt-1 font-heading text-3xl font-extrabold" style={{ color: tone || "#F8FAFC" }}>{value}</p>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function PartnersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [compTier, setCompTier] = useState({});
  const loadPartners = useCallback(() => {
    return api.get("/partners").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadPartners(); }, [loadPartners]);
  // Live: refetch when a new partner application lands in Supabase.
  useEffect(() => {
    const sb = getSupabase();
    if (!sb) return;
    const ch = sb
      .channel("compliance-partners")
      .on("postgres_changes", { event: "*", schema: "public", table: "partner_applications" }, () => loadPartners())
      .subscribe();
    return () => { sb.removeChannel(ch); };
  }, [loadPartners]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/partners/${id}/status`, { status });
      toast.success(`Application ${status}`);
      await loadPartners();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update status");
    } finally { setBusyId(null); }
  };

  const comp = async (id) => {
    const tier = compTier[id] || "elite";
    setBusyId(id);
    try {
      await api.post(`/partners/${id}/comp`, { tier });
      toast.success(`Comped ${tier} membership`);
      await loadPartners();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not comp this partner");
    } finally { setBusyId(null); }
  };

  const statusTone = (s) => (
    s === "approved" ? "bg-emerald-500/15 text-emerald-400"
      : s === "denied" ? "bg-rose-500/15 text-rose-400"
        : "bg-amber-500/15 text-amber-400"
  );

  return (
    <div className="rounded-2xl border border-[#2A313C] bg-[#1F242D] p-6" data-testid="partners-panel">
      <h3 className="font-heading text-xl font-semibold text-white flex items-center gap-2 mb-4">
        <Handshake className="w-5 h-5 text-[#FF5E3A]" /> Partner applications
      </h3>
      {loading ? (
        <p className="text-slate-400 text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-slate-400 text-sm">No partner applications yet</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-[#2A313C] bg-[#262D38] p-4" data-testid="partner-row">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold text-white">{r.full_name} <span className="text-slate-400 font-normal">· {r.partner_type}</span></p>
                  <p className="text-sm text-slate-400">{r.email} · {r.phone} · {r.city_region}</p>
                </div>
                <span className="font-mono text-xs px-2 py-1 rounded-lg bg-[#FF5E3A]/15 text-[#FF5E3A]">{r.assigned_referral_code}</span>
              </div>
              {r.pitch_vision && <p className="mt-2 text-sm text-slate-300">{r.pitch_vision}</p>}
              <p className="mt-2 text-xs text-slate-500">Reach: {r.influence_reach} · Interests: {(r.partnership_interests || []).join(", ") || "—"}</p>

              <div className="mt-3 flex items-center gap-2 flex-wrap border-t border-[#2A313C] pt-3">
                <span className={`text-xs px-2 py-1 rounded-lg font-medium capitalize ${statusTone(r.status)}`} data-testid="partner-status">
                  {r.status}
                </span>
                {r.comped_tier && (
                  <span className="text-xs px-2 py-1 rounded-lg bg-violet-500/15 text-violet-300 font-medium capitalize" data-testid="partner-comped-tier">
                    Comped: {r.comped_tier}
                  </span>
                )}
                {!r.user_id && (
                  <span className="text-xs px-2 py-1 rounded-lg bg-slate-500/15 text-slate-400">No Sparkd account linked</span>
                )}
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    disabled={busyId === r.id || r.status === "approved"}
                    onClick={() => setStatus(r.id, "approved")}
                    data-testid="partner-approve-btn"
                    className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white"
                  >Approve</Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === r.id || r.status === "denied"}
                    onClick={() => setStatus(r.id, "denied")}
                    data-testid="partner-deny-btn"
                    className="h-8 rounded-lg border-rose-500/40 text-rose-400 hover:bg-rose-500/10 bg-transparent"
                  >Deny</Button>
                  <Select value={compTier[r.id] || "elite"} onValueChange={(v) => setCompTier((p) => ({ ...p, [r.id]: v }))}>
                    <SelectTrigger className="h-8 w-[108px] bg-[#1F242D] border-[#2A313C] text-white rounded-lg" data-testid="partner-comp-tier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plus">Plus</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="elite">Elite</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => comp(r.id)}
                    data-testid="partner-comp-btn"
                    className="h-8 rounded-lg spark-gradient text-white"
                  >Comp</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("tickets");
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [deletions, setDeletions] = useState([]);
  const [staff, setStaff] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selected, setSelected] = useState(null); // { kind, item }
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "" });
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    const ticketQ = params.toString() ? `?${params.toString()}` : "";
    const delParams = new URLSearchParams(params);
    if (sourceFilter !== "all") delParams.set("source", sourceFilter);
    const delQ = delParams.toString() ? `?${delParams.toString()}` : "";
    try {
      const [s, t, d, st] = await Promise.all([
        api.get("/stats"),
        api.get(`/tickets${ticketQ}`),
        api.get(`/deletion-requests${delQ}`),
        api.get("/staff"),
      ]);
      setStats(s.data);
      setTickets(t.data);
      setDeletions(d.data);
      setStaff(st.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/staff/login");
    }
  }, [statusFilter, sourceFilter, navigate]);

  useEffect(() => { load(); }, [load]);

  // Live: Supabase Realtime pushes row changes on shared compliance tables so
  // the dashboard reflects new tickets / deletion requests without a refresh.
  useEffect(() => {
    const sb = getSupabase();
    if (!sb) return;
    const ch = sb
      .channel("compliance-dashboard")
      .on("postgres_changes", { event: "*", schema: "public", table: "compliance_tickets" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "compliance_deletion_requests" }, () => load())
      .subscribe();
    return () => { sb.removeChannel(ch); };
  }, [load]);

  const doLogout = async () => { await logout(); navigate("/staff/login"); };

  const openItem = (kind, item) => { setSelected({ kind, item }); setReply(""); };

  const changeStatus = async (kind, item, status) => {
    const base = kind === "ticket" ? "tickets" : "deletion-requests";
    try {
      const { data } = await api.patch(`/${base}/${item.id}/status`, { status });
      const updated = data.id ? data : data.ticket || data.request;
      toast.success("Status updated");
      if (selected) setSelected({ kind, item: updated });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    const base = selected.kind === "ticket" ? "tickets" : "deletion-requests";
    try {
      const { data } = await api.post(`/${base}/${selected.item.id}/reply`, { message: reply });
      const updated = data.ticket || data.request;
      setSelected({ ...selected, item: updated });
      setReply("");
      toast[data.email_sent ? "success" : "warning"](
        data.email_sent ? "Reply sent to user's email" : "Reply saved (email could not be sent)"
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const addStaff = async () => {
    try {
      await api.post("/staff", newStaff);
      toast.success("Staff member added");
      setNewStaff({ name: "", email: "", password: "" });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeStaff = async (id) => {
    try {
      await api.delete(`/staff/${id}`);
      toast.success("Staff member removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const item = selected?.item;

  return (
    <div className="min-h-screen bg-[#0F172A]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0F172A]/80 border-b border-[#2A313C]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-[#E11D48] to-[#F59E0B]">
              <Flame className="w-5 h-5 text-white" fill="white" />
            </span>
            <span className="font-heading font-extrabold text-white">Sparkd</span>
            <span className="text-xs text-slate-500 border-l border-[#2A313C] pl-2 ml-1">Staff Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-slate-400" data-testid="current-user">{user?.name || user?.email}</span>
            <Button variant="ghost" onClick={doLogout} data-testid="logout-btn"
              className="text-slate-300 hover:text-white hover:bg-white/5 gap-2">
              <LogOut className="w-4 h-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-8">
            <StatCard label="Open tickets" value={stats.tickets.open} tone="#F59E0B" />
            <StatCard label="Resolved tickets" value={stats.tickets.resolved} tone="#10B981" />
            <StatCard label="Deletion requests" value={stats.deletions.total} tone="#E11D48" />
            <StatCard label="In grace period" value={stats.deletions.pending_grace_period} tone="#A855F7" />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <TabsList className="bg-[#1F242D] border border-[#2A313C] p-1 rounded-xl">
              <TabsTrigger value="tickets" data-testid="tab-tickets" className="data-[state=active]:bg-[#E11D48] data-[state=active]:text-white rounded-lg gap-2">
                <Ticket className="w-4 h-4" /> Tickets
              </TabsTrigger>
              <TabsTrigger value="deletions" data-testid="tab-deletions" className="data-[state=active]:bg-[#E11D48] data-[state=active]:text-white rounded-lg gap-2">
                <Trash2 className="w-4 h-4" /> Deletions
              </TabsTrigger>
              <TabsTrigger value="team" data-testid="tab-team" className="data-[state=active]:bg-[#E11D48] data-[state=active]:text-white rounded-lg gap-2">
                <Users className="w-4 h-4" /> Team
              </TabsTrigger>
              <TabsTrigger value="partners" data-testid="tab-partners" className="data-[state=active]:bg-[#E11D48] data-[state=active]:text-white rounded-lg gap-2">
                <Handshake className="w-4 h-4" /> Partners
              </TabsTrigger>
            </TabsList>

            {tab !== "team" && (
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className={`${inputCls} w-44`} data-testid="status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#262D38] border-[#2A313C] text-white">
                    <SelectItem value="all" className="focus:bg-[#E11D48]/15">All statuses</SelectItem>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} className="focus:bg-[#E11D48]/15">{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {tab === "deletions" && (
                  <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger className={`${inputCls} w-44`} data-testid="source-filter"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#262D38] border-[#2A313C] text-white">
                      <SelectItem value="all" className="focus:bg-[#E11D48]/15">All sources</SelectItem>
                      <SelectItem value="web_form" className="focus:bg-[#E11D48]/15">Web Form</SelectItem>
                      <SelectItem value="marketing_site" className="focus:bg-[#E11D48]/15">Marketing Site</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>

          <TabsContent value="tickets">
            <ItemList kind="ticket" items={tickets} onOpen={openItem} emptyLabel="No support tickets yet" />
          </TabsContent>
          <TabsContent value="deletions">
            <ItemList kind="deletion" items={deletions} onOpen={openItem} emptyLabel="No deletion requests yet" />
          </TabsContent>

          <TabsContent value="partners">
            <PartnersPanel />
          </TabsContent>

          <TabsContent value="team">
            <div className={`${card} p-6`}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-white flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-[#E11D48]" /> Support team
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">Staff members who can sign in to this dashboard.</p>
                </div>
                <Dialog open={addOpen} onOpenChange={setAddOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-staff-btn" className="rounded-xl bg-gradient-to-r from-[#E11D48] to-[#F59E0B] text-white gap-2">
                      <Plus className="w-4 h-4" /> Add member
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1F242D] border-[#2A313C] text-white">
                    <DialogHeader><DialogTitle className="text-white">Add staff member</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Full name</Label>
                        <Input value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                          className={inputCls} data-testid="staff-name" placeholder="Jane Doe" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Email</Label>
                        <Input type="email" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                          className={inputCls} data-testid="staff-email" placeholder="jane@sparkd-app.com" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Temporary password</Label>
                        <Input type="text" value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                          className={inputCls} data-testid="staff-password" placeholder="At least 8 characters" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={addStaff} data-testid="staff-save"
                        className="rounded-xl bg-gradient-to-r from-[#E11D48] to-[#F59E0B] text-white">Create account</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-3" data-testid="staff-list">
                {staff.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-xl border border-[#2A313C] bg-[#262D38] px-4 py-3">
                    <div>
                      <p className="font-medium text-white flex items-center gap-2">
                        {s.name || s.email}
                        {s.role === "admin" && <span className="text-xs px-2 py-0.5 rounded-full bg-[#E11D48]/15 text-[#E11D48]">Admin</span>}
                      </p>
                      <p className="text-sm text-slate-400">{s.email}</p>
                    </div>
                    {s.role !== "admin" && s.id !== user?.id && (
                      <Button variant="ghost" size="sm" onClick={() => removeStaff(s.id)}
                        data-testid={`remove-staff-${s.id}`}
                        className="text-slate-400 hover:text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Detail / reply drawer */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-[#0F172A] border-l border-[#2A313C] w-full sm:max-w-lg overflow-y-auto p-0">
          {item && (
            <div>
              <SheetHeader className="p-6 border-b border-[#2A313C]">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-[#E11D48]">{item.ref}</span>
                  <StatusBadge status={item.status} />
                </div>
                <SheetTitle className="text-white text-left pt-2 font-heading">
                  {selected.kind === "ticket" ? item.subject : "Account deletion request"}
                </SheetTitle>
              </SheetHeader>

              <div className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">From</p>
                    <p className="text-white break-all" data-testid="detail-email">{item.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Submitted</p>
                    <p className="text-white">{fmtDate(item.created_at)}</p>
                  </div>
                  {selected.kind === "ticket" && (
                    <div>
                      <p className="text-slate-500">Category</p>
                      <p className="text-white">{item.category}</p>
                    </div>
                  )}
                  {selected.kind === "deletion" && (
                    <div>
                      <p className="text-slate-500">Source</p>
                      <p className="text-white" data-testid="detail-source">{sourceLabel(item.source)}</p>
                    </div>
                  )}
                  {selected.kind === "deletion" && item.status === "pending_grace_period" && item.days_remaining != null && (
                    <div>
                      <p className="text-slate-500">Auto-purge</p>
                      <p className="text-white" data-testid="detail-countdown">
                        {item.days_remaining === 0 ? "Today" : `${item.days_remaining} day(s) left`}
                        {item.purge_on ? ` · ${fmtDate(item.purge_on)}` : ""}
                      </p>
                    </div>
                  )}
                  {selected.kind === "deletion" && item.username && (
                    <div>
                      <p className="text-slate-500">Username / phone</p>
                      <p className="text-white">{item.username}</p>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-[#2A313C] bg-[#1F242D] p-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                    {selected.kind === "ticket" ? "Message" : "Reason"}
                  </p>
                  <p className="text-slate-200 whitespace-pre-wrap text-sm leading-relaxed">
                    {selected.kind === "ticket" ? item.message : (item.reason || "No reason provided.")}
                  </p>
                </div>

                {/* Status control */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Update status</Label>
                  <Select value={item.status} onValueChange={(v) => changeStatus(selected.kind, item, v)}>
                    <SelectTrigger className={inputCls} data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#262D38] border-[#2A313C] text-white">
                      {STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} className="focus:bg-[#E11D48]/15">{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Replies */}
                {item.replies?.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Conversation</p>
                    {item.replies.map((r) => (
                      <div key={r.id} className="rounded-xl border border-[#E11D48]/20 bg-[#E11D48]/5 p-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-white">{r.author}</span>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            {r.email_sent && <Mail className="w-3 h-3 text-[#10B981]" />}
                            {fmtDate(r.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-200 whitespace-pre-wrap">{r.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply box */}
                <div className="space-y-2 pt-2 border-t border-[#2A313C]">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#E11D48]" /> Reply to user (sends an email)
                  </Label>
                  <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)}
                    placeholder="Type your reply..." className={inputCls} data-testid="reply-message" />
                  <Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="reply-send"
                    className="w-full rounded-xl bg-gradient-to-r from-[#E11D48] to-[#F59E0B] text-white gap-2">
                    <Send className="w-4 h-4" /> {sending ? "Sending..." : "Send reply"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ItemList({ kind, items, onOpen, emptyLabel }) {
  if (!items.length) {
    return (
      <div className="rounded-2xl border border-dashed border-[#2A313C] bg-[#1F242D] py-20 text-center">
        <Inbox className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <p className="text-slate-400">{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid={`${kind}-list`}>
      {items.map((it) => (
        <button
          key={it.id}
          onClick={() => onOpen(kind, it)}
          data-testid={`${kind}-row-${it.ref}`}
          className="w-full text-left rounded-2xl border border-[#2A313C] bg-[#1F242D] p-5 hover:border-[#E11D48]/30 transition-all flex items-start justify-between gap-4"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs text-[#E11D48]">{it.ref}</span>
              {kind === "deletion" && (
                <span data-testid={`source-badge-${it.ref}`}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-[#2A313C] bg-[#262D38] text-slate-300">
                  {sourceLabel(it.source)}
                </span>
              )}
              {kind === "deletion" && it.status === "pending_grace_period" && it.days_remaining != null && (
                <span data-testid={`grace-countdown-${it.ref}`}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-[#A855F7]/30 bg-[#A855F7]/10 text-[#C99BF5]">
                  {it.days_remaining === 0 ? "Purges today" : `${it.days_remaining}d left`}
                </span>
              )}
              {it.replies?.length > 0 && (
                <span className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" />{it.replies.length}</span>
              )}
            </div>
            <p className="font-medium text-white truncate">
              {kind === "ticket" ? it.subject : `Deletion request — ${it.email}`}
            </p>
            <p className="text-sm text-slate-400 truncate mt-0.5">
              {it.email}{kind === "ticket" ? ` · ${it.category}` : ""}
            </p>
          </div>
          <StatusBadge status={it.status} />
        </button>
      ))}
    </div>
  );
}
